import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaChevronRight } from "react-icons/fa";

import {
  type AiInsightsResponse,
  getAiInsightsAvailabilityMessage,
  useAiInsightJob,
} from "../../../api/aiInsights";
import { isApiError } from "../../../api/errors";
import { useGetRaidInsightItems } from "../../../api/raid";
import { formatDisplayDateValue } from "../../../utils/dateDisplay";
import { AiOutputFeedbackControls } from "../../aiOutputFeedback/AiOutputFeedbackControls";
import { Spinner } from "../../shared";
import { AiInsightDeveloperContext, AiInsightSourceMarker } from "../AiInsightsRaidDetailParts";
import { AiInsightWaitingState } from "../AiInsightWaitingState";
import { AiInsightsDecisionsDetailModal } from "./AiInsightsDecisionsDetailModal";
import type {
  DecisionsInsightDto,
  DecisionsPotentialUnrecordedItemDto,
  DecisionsTopItemDto,
} from "./AiInsightsDecisionsTypes";

/** Backend reason code (Carter.Domain.Exceptions.NotFoundException) surfaced when the
 * current user has no active AI Insights persona assignment. */
const PERSONA_NOT_ASSIGNED_ERROR_CODE = "no_persona";

export type AiInsightsDecisionsShelfProps = {
  /** Whether the shared global insights overlay panel is currently open. */
  isOpen: boolean;
  insightResponse?: AiInsightsResponse;
};

/**
 * Renders the real, API-backed Decisions insight inside the global insights
 * overlay: durable request-and-poll, loading/error states, and a
 * one-time toast when the current user has no AI Insights persona assigned.
 * Mirrors AiInsightsRaidRisksIssuesShelf's structure/behavior for the
 * decisions pageType -- see docs/decision-log.md.
 */
export function AiInsightsDecisionsShelf({
  isOpen,
  insightResponse,
}: AiInsightsDecisionsShelfProps) {
  // Only request the panel's job once the overlay is actually open.
  const decisionsQuery = useAiInsightJob("decisions", isOpen && insightResponse == null);

  // Tracks which single "potential unrecorded decision" candidate card is
  // expanded; index-based since candidates don't carry a stable id.
  const [expandedUnrecordedIndex, setExpandedUnrecordedIndex] = useState<number | null>(null);
  const toggleUnrecordedIndex = (index: number) => {
    setExpandedUnrecordedIndex((current) => (current === index ? null : index));
  };

  // The decision card currently shown in the detail popup modal, if any.
  const [selectedDecision, setSelectedDecision] = useState<DecisionsTopItemDto | null>(null);

  const decisionsResponse = insightResponse ?? decisionsQuery.data?.data;
  const decisionsInsight = decisionsResponse?.insight as DecisionsInsightDto | undefined;
  const decisionLookupIds = useMemo(
    () =>
      (Array.isArray(decisionsInsight?.top_decisions) ? decisionsInsight.top_decisions : [])
        .map((item) => item.decision_id?.trim() ?? "")
        .filter(Boolean),
    [decisionsInsight]
  );
  const decisionSourcesQuery = useGetRaidInsightItems(
    decisionLookupIds,
    isOpen && decisionsInsight != null,
    decisionsResponse?.dealDataVersionId
  );
  const decisionSourcesById = useMemo(
    () =>
      new Map(
        (decisionSourcesQuery.data ?? []).map((item) => [item.itemId.toLowerCase(), item])
      ),
    [decisionSourcesQuery.data]
  );
  const isDecisionsLoading = isOpen && decisionsQuery.isLoading && decisionsInsight == null;
  const decisionsUnderlyingError = decisionsQuery.error;
  const isPersonaNotAssignedError =
    isApiError(decisionsUnderlyingError) &&
    decisionsUnderlyingError.code === PERSONA_NOT_ASSIGNED_ERROR_CODE;
  const isNoDataError =
    isApiError(decisionsUnderlyingError) && decisionsUnderlyingError.code === "no_data";
  const isReadinessWaiting = decisionsQuery.mappingReason != null;
  const decisionsError =
    isOpen && !isDecisionsLoading && decisionsInsight == null
      ? isPersonaNotAssignedError
        ? "No AI Insights persona is assigned to your account yet."
        : decisionsQuery.isError && !isNoDataError && !isReadinessWaiting
          ? "Unable to load insights right now. Please try again shortly."
          : null
      : null;
  const decisionsUnavailableMessage =
    isOpen && !isDecisionsLoading && decisionsInsight == null
      ? getAiInsightsAvailabilityMessage(
          isApiError(decisionsUnderlyingError) ? decisionsUnderlyingError.code : undefined,
          "Decisions"
        )
      : null;

  // Surface a one-time toast when the panel is opened and the user has no
  // persona assigned, per explicit user direction: background requests must stay
  // silent (see App.tsx), but an *interactive* overlay open should tell the
  // user why no insight is rendering. Guarded by a ref so re-renders while
  // the panel stays open don't spam duplicate toasts.
  const hasShownPersonaToastRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasShownPersonaToastRef.current = false;
      return;
    }
    if (isPersonaNotAssignedError && !hasShownPersonaToastRef.current) {
      hasShownPersonaToastRef.current = true;
      toast.error(
        "No AI Insights persona is assigned to your account yet. Contact an admin to get one assigned."
      );
    }
  }, [isOpen, isPersonaNotAssignedError]);

  if (isDecisionsLoading) {
    return (
      <section
        className="global-insights-overlay__section global-insights-overlay__section--loading"
        aria-live="polite"
      >
        <Spinner size="lg" />
      </section>
    );
  }

  if (decisionsError != null) {
    return (
      <section className="global-insights-overlay__section" aria-live="assertive">
        <p className="global-insights-overlay__raid-status-text global-insights-overlay__raid-status-text--error">
          {decisionsError}
        </p>
      </section>
    );
  }

  if (decisionsUnavailableMessage != null) {
    return isReadinessWaiting ? (
      <AiInsightWaitingState reason={decisionsQuery.mappingReason} />
    ) : (
      <section className="global-insights-overlay__section" aria-live="polite">
        <p className="global-insights-overlay__raid-status-text">{decisionsUnavailableMessage}</p>
      </section>
    );
  }

  if (decisionsInsight == null) {
    return null;
  }

  const topDecisions = Array.isArray(decisionsInsight.top_decisions)
    ? decisionsInsight.top_decisions
    : [];
  const potentialUnrecordedDecisions = Array.isArray(
    decisionsInsight.potential_unrecorded_decisions
  )
    ? decisionsInsight.potential_unrecorded_decisions
    : [];

  return (
    <>
      <section
        className="global-insights-overlay__section global-insights-overlay__section--raid-health"
        aria-labelledby="global-insights-decisions-health-title"
      >
        <div className="global-insights-overlay__section-header">
          <h3
            id="global-insights-decisions-health-title"
            className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
          >
            Decisions health
          </h3>
          <AiInsightSourceMarker />
        </div>
        <div className="global-insights-overlay__raid-health-card">
          <p className="global-insights-overlay__raid-health-text">
            {decisionsInsight.summary ?? "Not available"}
          </p>
        </div>
        {decisionsResponse?.status === "ready" && (
          <AiOutputFeedbackControls
            executionId={decisionsResponse.aiOutputExecutionId}
            outputType="ai_insight"
            className="global-insights-overlay__feedback"
          />
        )}
      </section>

      <section
        className="global-insights-overlay__section"
        aria-labelledby="global-insights-decisions-drivers-title"
      >
        <h3
          id="global-insights-decisions-drivers-title"
          className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
        >
          Top decisions requiring attention
        </h3>

        {topDecisions.length > 0 ? (
          <div className="global-insights-overlay__raid-driver-list">
            {topDecisions.map((item, index) => {
              const itemMeta =
                [item?.why_attention_is_needed_now].filter(Boolean).join(" · ") || "Not available";

              return (
                <article
                  key={item?.decision_id ?? item?.title ?? `decision-${index}`}
                  className="global-insights-overlay__raid-driver-card global-insights-overlay__raid-driver-card--clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedDecision(item)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedDecision(item);
                    }
                  }}
                >
                  <div className="global-insights-overlay__raid-driver-copy">
                    <div className="global-insights-overlay__raid-driver-header-row">
                      <span className="global-insights-overlay__raid-driver-item-id">
                        {item?.decision_id ?? "Not available"}
                      </span>
                    </div>
                    <h4 className="global-insights-overlay__raid-driver-title global-insights-overlay__raid-driver-title--with-chevron">
                      <span>{item?.title ?? "Not available"}</span>
                      <FaChevronRight aria-hidden="true" />
                    </h4>
                    <p className="global-insights-overlay__raid-driver-type global-insights-overlay__raid-driver-type--aligned">
                      <span className="global-insights-overlay__raid-driver-meta-item">{itemMeta}</span>
                      {item?.owner && (
                        <span className="global-insights-overlay__raid-driver-meta-item global-insights-overlay__raid-driver-owner">
                          Owner: {item.owner}
                        </span>
                      )}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="global-insights-overlay__raid-status-text">No top decisions available.</p>
        )}
      </section>

      <section
        className="global-insights-overlay__section"
        aria-labelledby="global-insights-decisions-unrecorded-title"
      >
        <h3
          id="global-insights-decisions-unrecorded-title"
          className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
        >
          Potential unrecorded decisions
        </h3>

        {potentialUnrecordedDecisions.length > 0 ? (
          <div className="global-insights-overlay__raid-unrecorded-list">
            {potentialUnrecordedDecisions.map((candidate, index) => (
              <UnrecordedDecisionCard
                key={candidate?.suggested_title ?? `potential-unrecorded-decision-${index}`}
                candidate={candidate}
                isExpanded={expandedUnrecordedIndex === index}
                onToggle={() => toggleUnrecordedIndex(index)}
              />
            ))}
          </div>
        ) : (
          <p className="global-insights-overlay__raid-status-text">
            No potential unrecorded decisions available.
          </p>
        )}
      </section>

      <AiInsightDeveloperContext
        developerContext={{
          asAt: decisionsInsight.as_at,
          dataUsed: decisionsInsight.context_summary?.data_used,
          missingInputs: decisionsInsight.context_summary?.data_missing_or_restricted,
          limitations: decisionsInsight.data_quality_and_caveats,
        }}
      />

      {selectedDecision != null && (
        <AiInsightsDecisionsDetailModal
          item={selectedDecision}
          lastUpdatedAt={
            decisionSourcesById.get(selectedDecision.decision_id?.toLowerCase() ?? "")?.lastUpdatedAt
          }
          onClose={() => setSelectedDecision(null)}
        />
      )}
    </>
  );
}

export type UnrecordedDecisionCardProps = {
  /** A single "potential unrecorded decision" candidate returned by the LLM. */
  candidate: DecisionsPotentialUnrecordedItemDto;
  /** Whether this candidate's collapsible detail body is currently expanded. */
  isExpanded: boolean;
  /** Toggles the expanded state; only one candidate is expanded at a time. */
  onToggle: () => void;
};

/**
 * Collapsible card for a single "potential unrecorded decision" candidate.
 * Renders a summary header (confidence and suggested title) that is always
 * visible, with the fuller LLM-provided detail (decision required, why it is
 * needed, owner, due date, and potential impact if not decided) revealed on
 * expand. Mirrors AiInsightsRaidRisksIssuesShelf's UnrecordedRiskCard
 * structure/behavior for the decisions pageType.
 */
export function UnrecordedDecisionCard({
  candidate,
  isExpanded,
  onToggle,
}: UnrecordedDecisionCardProps) {
  return (
    <div className="global-insights-overlay__raid-unrecorded-card">
      <button
        type="button"
        className="global-insights-overlay__raid-unrecorded-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="global-insights-overlay__raid-unrecorded-summary">
          <p className="global-insights-overlay__raid-unrecorded-meta">
            <span>{`Confidence: ${candidate?.confidence ?? "Not available"}`}</span>
          </p>
          <h4 className="global-insights-overlay__raid-unrecorded-title">
            {candidate?.suggested_title ?? "Not available"}
          </h4>
        </div>
        <span
          className={[
            "global-insights-overlay__raid-unrecorded-chevron",
            isExpanded ? "global-insights-overlay__raid-unrecorded-chevron--expanded" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          <span className="Appkit4-icon icon-down-chevron-outline" />
        </span>
      </button>

      {isExpanded && (
        <div className="global-insights-overlay__raid-unrecorded-content">
          <p className="global-insights-overlay__raid-unrecorded-label">What decision is needed</p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate?.decision_required ?? "Not available"}
          </p>

          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">
            Why this decision is needed
          </p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate?.why_decision_is_needed ?? "Not available"}
          </p>

          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <div className="global-insights-overlay__raid-unrecorded-owner-row">
            <div className="global-insights-overlay__raid-unrecorded-owner-col">
              <p className="global-insights-overlay__raid-unrecorded-label">Decision owner</p>
              <p className="global-insights-overlay__raid-unrecorded-text">
                {candidate?.likely_owner ?? "Not available"}
              </p>
            </div>
            <div className="global-insights-overlay__raid-unrecorded-owner-col global-insights-overlay__raid-unrecorded-owner-col--right">
              <p className="global-insights-overlay__raid-unrecorded-label">Decision due</p>
              <p className="global-insights-overlay__raid-unrecorded-text">
                {formatDisplayDateValue(candidate?.needed_by_date, { fallback: "Not available" })}
              </p>
            </div>
          </div>

          <p className="global-insights-overlay__risk-detail-warning-box">
            <span className="global-insights-overlay__raid-unrecorded-label">
              Impact if not decided on time
            </span>
            <br />
            {candidate?.potential_impact_if_not_decided ?? "Not available"}
          </p>
        </div>
      )}
    </div>
  );
}

export default AiInsightsDecisionsShelf;
